import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { editApkReducer } from './editApkReducer';
import { EditApkContext, editApkInitialState, TTypeOfEditApkModalWindow } from './editApkContext';
import { EditApkActionTypes } from './editApkActions';
import { AppContext, IAppContext } from 'appState/appContext';
import { useFetchData } from 'hooks/useFetchData';
import { getApkInfo } from 'backend/apk';
import { IFileData, IEditApk, IGetApkInfo, TApkType, TParametersOfApkType } from 'types';
import { apkTypes, extraApkTypes } from 'appConstants';

const EditApkState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(editApkReducer, editApkInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);

	const params = new URL(document.location.href).searchParams;
	const apkID = params.get('apkID');

	const { response, loading, refetch } = useFetchData<IGetApkInfo, string>(apkID ? getApkInfo : null, apkID || '');

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: EditApkActionTypes.REFETCH_DATA,
				payload: {
					needRefetch: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			const mainTypes = apkTypes.map((apk) => apk.apkTypeID);
			const extraTypes = extraApkTypes.map((apk) => apk.apkTypeID);
			const parameterOfApkType = () => {
				if (mainTypes.includes(response.data.type)) {
					return 'main';
				}
				if (extraTypes.includes(response.data.type)) {
					return 'extra';
				}
				return null;
			};

			dispatch({
				type: EditApkActionTypes.SET_ALL_DATA,
				payload: {
					apkID: response.data.id,
					versionName: response.data.versionName || '',
					versionCode: String(response.data.versionCode),
					description: response.data.description || '',
					applicationId: response.data.applicationId || '',
					parameterOfApkType: parameterOfApkType(),
					IDApkType: response.data.type,
					isCutoverBeforeUpdate: response.data.isCutoverBeforeUpdate,
					filePath: response.data.path || '',
				},
			});
			dispatch({
				type: EditApkActionTypes.SET_INITIAL_FORM_DATA,
				payload: {
					initialFormData: {
						versionName: response.data.versionName || '',
						versionCode: String(response.data.versionCode),
						description: response.data.description || '',
						applicationId: response.data.applicationId || '',
						IDSelectedApkType: response.data.type,
						isCutoverBeforeUpdate: response.data.isCutoverBeforeUpdate,
						filePath: response.data.path || '',
					},
				},
			});
		}
	}, [response]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: EditApkActionTypes.REFETCH_DATA,
			payload: {
				needRefetch: value,
			},
		});
	}, []);

	const changeVersionName = useCallback((value: string) => {
		dispatch({
			type: EditApkActionTypes.CHANGE_VERSION_NAME,
			payload: {
				versionName: value,
			},
		});
	}, []);

	const changeVersionCode = useCallback((value: string) => {
		dispatch({
			type: EditApkActionTypes.CHANGE_VERSION_CODE,
			payload: {
				versionCode: value,
			},
		});
	}, []);

	const changeDescription = useCallback((value: string) => {
		dispatch({
			type: EditApkActionTypes.CHANGE_DESCRIPTION,
			payload: {
				description: value,
			},
		});
	}, []);

	const changeApplicationId = useCallback((value: string) => {
		dispatch({
			type: EditApkActionTypes.CHANGE_APPLICATION_ID,
			payload: {
				applicationId: value,
			},
		});
	}, []);

	const setParameterOfApkType = useCallback((value: Nullable<TParametersOfApkType>) => {
		dispatch({
			type: EditApkActionTypes.SET_PARAMETER_OF_APK_TYPE,
			payload: {
				parameterOfApkType: value,
			},
		});
	}, []);

	const setIDApkType = useCallback((value: Nullable<TApkType>) => {
		dispatch({
			type: EditApkActionTypes.SET_ID_APK_TYPE,
			payload: {
				IDApkType: value,
			},
		});
	}, []);

	const toggleCutoverBeforeUpdate = useCallback(() => {
		dispatch({
			type: EditApkActionTypes.TOGGLE_CUTOVER_BEFORE_UPDATE,
		});
	}, []);

	const changeFileData = useCallback((value: IFileData) => {
		dispatch({
			type: EditApkActionTypes.CHANGE_FILE_DATA,
			payload: {
				fileData: value,
			},
		});
	}, []);

	const setDataForSubmit = useCallback((value: IEditApk | null) => {
		dispatch({
			type: EditApkActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfEditApkModalWindow) => {
		dispatch({
			type: EditApkActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: EditApkActionTypes.RESET_FIELDS }), []);

	return (
		<EditApkContext.Provider
			value={{
				refetchData,
				changeVersionName,
				changeVersionCode,
				changeDescription,
				changeApplicationId,
				setParameterOfApkType,
				setIDApkType,
				toggleCutoverBeforeUpdate,
				changeFileData,
				setDataForSubmit,
				changeTypeOfModalWindow,
				resetFields,
				needRefetchData: state.needRefetchData,
				initialFormData: state.initialFormData,
				apkID: state.apkID,
				versionName: state.versionName,
				versionCode: state.versionCode,
				description: state.description,
				applicationId: state.applicationId,
				parameterOfApkType: state.parameterOfApkType,
				IDSelectedApkType: state.IDSelectedApkType,
				isCutoverBeforeUpdate: state.isCutoverBeforeUpdate,
				fileData: state.fileData,
				filePath: state.filePath,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</EditApkContext.Provider>
	);
};

export default EditApkState;
